import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { statusLabel } from './paymentUtils';

export const BackfillDialog = ({ open, onClose, apiFetch, apiUrl, onDone }) => {
  const [days, setDays] = useState(365);
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const poll = (jobId) => {
    timer.current = setInterval(async () => {
      const resp = await apiFetch(`${apiUrl}/api/billpay/payments/backfill/${jobId}`);
      if (!resp.ok) return;
      const data = await resp.json();
      setJob(data);
      if (data.status !== 'running') {
        clearInterval(timer.current);
        onDone();
      }
    }, 3000);
  };

  const start = async () => {
    setError('');
    const resp = await apiFetch(`${apiUrl}/api/billpay/payments/backfill?days=${days}`, { method: 'POST' });
    const data = await resp.json();
    if (!resp.ok) return setError(data.detail || 'Backfill failed to start');
    setJob(data);
    poll(data.job_id);
  };

  const running = job?.status === 'running';
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !running && onClose()}>
      <DialogContent data-testid="backfill-dialog" className="max-w-md">
        <DialogHeader><DialogTitle>Backfill history</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">
          Saves past Mercury payments with their match result. It never posts to QuickBooks and never sends anything to Mercury.
          Rows that would post are saved as Needs Review.
        </p>
        <label className="text-sm text-slate-700 flex items-center gap-2">
          Days back
          <input type="number" min={1} max={365} data-testid="backfill-days-input" value={days} disabled={running}
            onChange={(e) => setDays(Math.min(365, Math.max(1, Number(e.target.value) || 1)))}
            className="w-24 border rounded-md px-2 py-1.5" />
        </label>
        {error && <p className="text-sm text-red-600" data-testid="backfill-error">{error}</p>}
        {running && <p className="text-sm text-slate-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Running…</p>}
        {job?.status === 'failed' && <p className="text-sm text-red-600">Failed: {job.error}</p>}
        {job?.status === 'done' && (
          <div className="text-sm bg-slate-50 rounded-md p-3" data-testid="backfill-summary">
            <p className="font-medium mb-1">{job.fetched} payments checked, {job.new_rows} new rows</p>
            {Object.entries(job.summary || {}).map(([k, v]) => <p key={k}>{statusLabel(k)}: {v}</p>)}
          </div>
        )}
        <DialogFooter>
          <button type="button" onClick={onClose} disabled={running} className="px-4 py-2 text-sm border rounded-md">Close</button>
          <button type="button" data-testid="backfill-start-btn" onClick={start} disabled={running}
            className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white disabled:opacity-50">Run backfill</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
